import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useCustomer } from 'lib/core/hooks'
import { Spinner } from 'lib/ui/components'
import { usePdfDownload } from 'hooks/usePdfDownload'
import { ReactComponent as ArrowLeft } from 'resources/svg/icons/angle-left.svg'
import { PRODUCT_SELECTOR_CODE } from 'app/livbrite/resources/constants'
import { PublicLayout } from 'app/livbrite/components/PublicLayout'
import { Title } from '../../styled'
import { Container, Actions } from './styled'
import { TitleWrapper, DropDown } from 'app/livbrite/scenes/Customer/scenes/Account/scenes/Community/scenes/Dashboard/styled'
import { useReportDetail } from 'hooks/reports'
import { PDF } from './components/PDF'
import { DateRange } from './components/DateRange'
import { ReportInfo } from './components/ReportInfo'

interface RouteParams {
  id: string
}

export const ReportChart = () => {
  const history = useHistory<{ reportNumber?: string }>()
  const params = useParams<RouteParams>()
  const { downloadPDF } = usePdfDownload()
  const [dateRange, setDateRange] = useState<string | undefined>()
  const [communityIndex, setCommunityIndex] = useState(0)
  const { customer } = useCustomer()
  const id = parseInt(params.id)
  const { getReport, report, reportLoading: loading } = useReportDetail()
  const pdf = useRef<HTMLDivElement | null>(null)
  // @ts-expect-error
  const communities = Object.assign([], customer?.communities)

  if (communities.length > 1) {
    communities.unshift({
      code: 'cc0',
      communityId: 0,
      name: 'Your Portfolio'
    })
  }

  const communityId = communities?.[communityIndex].communityId ?? 0
  const selectedCommunityName = communities?.[communityIndex].name

  const [customerEmail, setCustomerEmail] = useState<string | undefined>()

  const reportUrl = report?.url

  const handleDateSubmit = (values: any) => {
    const { fromDate, toDate } = values
    setCustomerEmail(undefined)
    const dateRange = `${formatDate(fromDate)} - ${formatDate(toDate)}`
    setDateRange(dateRange)
  }

  useEffect(() => {
    getReport(
      {
        variables: {
          code: PRODUCT_SELECTOR_CODE.SURVEY,
          question_id: id,
          community_id: communityId,
          email: customerEmail,
          name: customer?.firstName,
          json: !customerEmail,
          date_range: dateRange
        }
      }
    )
    window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })

    // eslint-disable-next-line
  }, [customerEmail, communityId, dateRange])

  useEffect(() => {
    if (report && !loading) {
      setCustomerEmail(undefined)
    }
  }, [report, loading])

  const sendEmail = () => {
    setCustomerEmail(customer?.email)
  }

  return (
    <>
      <PublicLayout className='general-content'>
        <div>
          {loading ? (
            <Spinner />
          ) : (
            <div>
              <Container>
                <Title
                  onClick={() => history.push('/customer/account/community')}
                  action
                >
                  <ArrowLeft />
                  Report {id}
                </Title>
                <TitleWrapper>
                  <h1>
                    Community report
                  </h1>
                  {communities.length > 1 && (
                    <DropDown onChange={e => setCommunityIndex(parseInt(e.target.value, 10))} value={communityIndex}>
                      {communities.map((community: any, index: number) => (
                        <option value={index} key={index}>{community.name}</option>
                      ))}
                    </DropDown>
                  )}
                </TitleWrapper>
                <DateRange
                  onSubmit={handleDateSubmit}
                  multipleRanges={false}
                />
                {report && (
                  <ReportInfo report={report} isPDF={false} />
                )}
                {!report && customerEmail && (
                  <div>
                    <h1>
                      The report was sent to your email
                    </h1>
                  </div>
                )}
                <Actions>
                  <a
                    href={reportUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    download
                  >
                    Download Report as CSV
                  </a>
                  <button
                    onClick={() => {
                      downloadPDF(pdf.current, `Report - ${report?.title}.pdf`)
                    }}
                  >
                    Download Report as PDF
                  </button>
                  <button
                    onClick={sendEmail}
                  >
                    Email Report
                  </button>
                </Actions>
              </Container>
            </div>
          )}
        </div>
      </PublicLayout>
      <PDF
        pdf={pdf}
        report={report}
        community={selectedCommunityName}
        dateRange={dateRange}
      />
    </>
  )
}

const formatDate = (date: Date): string => {
  const d = new Date(date)
  const year = d.getFullYear()
  let month = `${(d.getMonth() + 1)}`
  let day = `${d.getDate()}`

  if (month.length < 2) {
    month = `0${month}`
  }

  if (day.length < 2) {
    day = `0${day}`
  }

  return [month, day, year].join('/')
}
